import { ArrowUpRight, CalendarDays, CheckCircle2, Clock3, QrCode, Sparkles, UsersRound } from "lucide-react";
import { roleLabel, type MobileRole } from "../../lib/access";
import { firstName } from "../../lib/format";
import type { AppTabId } from "../../lib/navigation";

interface HomeTabProps {
  readonly role: MobileRole;
  readonly ctName: string;
  readonly email: string | null;
  readonly onNavigate: (tab: AppTabId) => void;
}

export function HomeTab({ role, ctName, email, onNavigate }: HomeTabProps) {
  const isStudent = role === "member";
  const name = firstName(email);

  return (
    <div className="tab-page">
      <section className="hero">
        <div className="hero__top">
          <span className="hero__eyebrow">{roleLabel(role).toUpperCase()} · APOGEU APP</span>
          <span className="hero__orbit" aria-hidden="true" />
        </div>
        <h1 className="hero__title">Olá, {name}.</h1>
        <p className="hero__subtitle">
          {isStudent
            ? `Acompanhe suas aulas e reservas no ${ctName}.`
            : `Sua agenda e suas turmas no ${ctName}, em um só lugar.`}
        </p>
        <div className="hero__actions">
          <button type="button" className="hero__button" onClick={() => onNavigate("agenda")}>
            <CalendarDays size={18} aria-hidden="true" />
            {isStudent ? "Ver aulas" : "Ver agenda"}
            <ArrowUpRight size={16} aria-hidden="true" />
          </button>
          <button type="button" className="hero__ghost" onClick={() => onNavigate("activity")}>
            {isStudent ? "Minhas reservas" : "Minhas turmas"}
          </button>
        </div>
      </section>

      <section className="section">
        <div className="section__heading">
          <h2 className="section__title">{isStudent ? "Próximo treino" : "Seu dia"}</h2>
          <span className="section__hint">Em breve</span>
        </div>
        <div className="next-card">
          <div className="next-card__icon">
            <Clock3 size={22} aria-hidden="true" />
          </div>
          <div className="next-card__body">
            <strong>{isStudent ? "Sua próxima aula aparece aqui" : "Sua agenda do dia aparece aqui"}</strong>
            <span>
              {isStudent
                ? "Quando a agenda do seu CT estiver conectada, você verá horário, quadra e professor."
                : "Quando a agenda do seu CT estiver conectada, você verá horários, quadras e alunos."}
            </span>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section__heading">
          <h2 className="section__title">Atalhos</h2>
        </div>
        <div className="shortcut-grid">
          <div className="shortcut-card">
            <span className="shortcut-card__icon"><QrCode size={21} aria-hidden="true" /></span>
            <strong>Check-in</strong>
            <span>Chegue e registre sua presença.</span>
            <span className="chip">Em breve</span>
          </div>
          <div className="shortcut-card">
            <span className="shortcut-card__icon">
              {isStudent ? <Sparkles size={21} aria-hidden="true" /> : <UsersRound size={21} aria-hidden="true" />}
            </span>
            <strong>{isStudent ? "Evolução" : "Turmas"}</strong>
            <span>{isStudent ? "Acompanhe sua frequência." : "Veja seus alunos por turma."}</span>
            <span className="chip">Em breve</span>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section__heading">
          <h2 className="section__title">Tudo em dia</h2>
        </div>
        <div className="info-card">
          <CheckCircle2 size={20} color="#7FA200" aria-hidden="true" />
          <div>
            <strong>Seu acesso está ativo</strong>
            <span>Você está conectado ao {ctName}. Novos recursos serão liberados aqui.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
